//@flow
import React from 'react';
import type { 
    UserTableColumn, 
    UserTableAction, 
    UserTableData,
    State
} from './types';
import type { Node } from 'react';
import SVGInline from 'react-svg-inline';
import sortIcon from '../../images/caret-down.svg';
import { Typography, theme } from '@hiver/hiver-ui-kit';

export const getIndexesInRange = (to: number, from: number) => {
    const indexes  = [];
    const inc = to > from ? 1 : -1;
    const conditional = i => to > from ? i <= to : i >= to;
    for(let i = from; conditional(i); i += inc) {
        indexes.push(i);
    }
    return indexes;
}
export const getRowCell = (
    item: Object, 
    accessor: string | Function,
    Cell: Function | null | undefined
) => {
    if (!item || !accessor)
        return '';
    let retVal;
    if (typeof accessor === 'string')
        retVal = item[accessor];
    else if (typeof accessor === 'function')
        retVal = accessor(item);
    if(Cell) {
        return Cell(retVal);
    } else
        return retVal;
};

export const renderHeaderCells = (
    columns: Array<UserTableColumn>,
    classes: Object,
    onSort?: Function,
    sortKey: $PropertyType<State, 'sortKey'>,
    sortOrder: $PropertyType<State, 'sortOrder'>,
): Array<Node> => {
    const cellClass = `h-tcell h-thead-cell h-flexbox--vc ${classes.cell}`;
    // const sortableClass = `h-tcell h-thead-cell ${classes.cell}`;
    return columns.map((item, i) => {
        let sortIconClass = 'h-pointer h-ml-13p';
        if (sortKey === item.accessor ) {
            sortIconClass = `${sortIconClass} active`
            if (sortOrder === -1)
                sortIconClass = `${sortIconClass} h-rotate-180`;
        } 
        return (
            <div key={`user-table-header-${i}`} 
                className={cellClass}>
                <span>
                    <Typography variant="overline" color={theme.palette.gray.black}>
                        {item.label}
                    </Typography>
                </span>
                { item.sortable && <SVGInline 
                        className={sortIconClass}
                        onClick={() => onSort?onSort(item): ''}
                        svg={sortIcon} />
                }
            </div>
        );
    });
}

export const renderHeaderActions = (
    actions: Array<UserTableAction>,
    classes: Object,
    onAction: Function,
    cellClass: string
): Array<Node> => actions.map(action => (
    <div key={`user-table-action-${action.label}`}
        className={cellClass}>
        <div
            onClick={() => onAction(action)}
            className={(action.classes ? action.classes : classes.actionBtn)}>
            {action.icon && <SVGInline component="div" svg={action.icon} />}
            <span>
                <Typography variant="overline" color={theme.palette.gray.black}>
                    {action.label}
                </Typography>
            </span>
        </div>
    </div>
));

export const renderRowCells = (
    data: Object,
    columns: Array<UserTableColumn>,
    classes: Object
): Array<Node> => columns.map((col, idx) => {
    let className;
    if (col.align === 'center')
        className = `h-tcell ${classes.cell} ${classes.cellCenter} h-h3`;
    else if (col.align === 'right')
        className = `h-tcell ${classes.cell} ${classes.cellEnd} h-h3`;
    else
        className  = `h-tcell ${classes.cell} h-h3`;
    const fieldData = getRowCell(data, col.accessor, col.cell);
    const title = typeof fieldData !== 'object' ? fieldData : '';
    if (data.disabled)
        className = `${className} ${classes.disabledCell}`;
    if (typeof col.cellAction === 'function') {
        return (<div title={title} className={className} onClick={() => col.cellAction(data)} key={`user-table-row-item-${idx}`}>
            <Typography variant="body1" color={theme.palette.gray.black}>
                { fieldData }
            </Typography>
        </div>);
    } else
        return (<div title={title} className={className} key={`user-table-row-item-${idx}`}>
            <Typography variant="body1" color={theme.palette.gray.black}>
                { fieldData }
            </Typography>
        </div>);   
});

export const sortTable = (
    data: Array<UserTableData>, 
    accessor: $PropertyType<UserTableColumn, 'accessor'>, 
    sortOrder: number
): Array<UserTableData> => {
    if (!accessor || accessor === 'id')
        return data;
    const compareFunc = (a, b) => {
        if (a.isSourceUserForSm && !b.isSourceUserForSm) {
            return -1; // Source user should always come first
        }
        if (!a.isSourceUserForSm && b.isSourceUserForSm) {
            return 1; // Source user should always come first
        }
        let A = getRowCell(a, accessor);
        let B = getRowCell(b, accessor);
        A = typeof A === 'string' ? A.toLowerCase(): A;
        B = typeof B === 'string' ? B.toLowerCase(): B;
        if (A > B)
            return sortOrder;
        if (A < B)
            return -sortOrder;
        return 0;
    };
    return [...data].sort(compareFunc);
};
